import type { ComuneNearby, ComuneOption } from '@aimmo/contracts';
import { api, assertOk } from '../lib/api.js';
import { makeLookup } from '../lib/lookup.js';
import { queryClient } from '../lib/query-client.js';

const COMUNI_KEY = ['geo', 'comuni'] as const;

/**
 * The comune picker, shared by every screen that points at a place.
 *
 * Always a typed search against the API, never a loaded list: there are 7.896
 * comuni, and a select with 7.896 options is a select nobody can use. They are
 * reference data, so an answer is never asked twice.
 */
export const { search: searchComuni, resolve: resolveComune } = makeLookup({
  key: COMUNI_KEY,
  staleTime: Infinity,
  search: async (query, signal) =>
    assertOk(
      await api.geo.comuni({
        query: { limit: 20, ...(query ? { q: query } : {}) },
        fetchOptions: { signal },
      }),
    ).body,
  get: (id, signal) => fetchComune(id, signal),
  toOption: (comune) => ({ value: comune.id, label: `${comune.name} (${comune.sigla})` }),
});

async function fetchComune(id: string, signal?: AbortSignal): Promise<ComuneOption> {
  return assertOk(await api.geo.comune({ params: { id }, fetchOptions: { signal } })).body;
}

/** How many neighbours one comune contributes at most: a ring, not a province. */
const NEARBY_LIMIT = 30;

/**
 * The comuni within `radiusKm` of each of `ids`, nearest first per centre.
 *
 * Over the centroids the ISTAT seed carries (docs/domain/02-geo.md): "Monza e
 * dintorni" is a radius around Monza's point, which is what a buyer means and
 * what the matching's own comune filter can then use as a plain list. The
 * centres come through the picker's cache, so a comune already on screen is
 * not asked for twice.
 */
export async function nearbyComuni(
  ids: readonly string[],
  radiusKm: number,
): Promise<ComuneNearby[]> {
  const radius = Math.round(Math.min(Math.max(radiusKm, 0.1), 200) * 1000);
  const rings = await Promise.all(
    ids.map(async (id) => {
      const centre = await queryClient.fetchQuery({
        queryKey: [...COMUNI_KEY, 'lookup', 'item', id],
        queryFn: ({ signal }) => fetchComune(id, signal),
        staleTime: Infinity,
      });
      return queryClient.fetchQuery({
        queryKey: [...COMUNI_KEY, 'nearby', id, radius],
        queryFn: async ({ signal }) =>
          assertOk(
            await api.geo.comuniNearby({
              query: { lat: centre.lat, lng: centre.lng, radius, limit: NEARBY_LIMIT },
              fetchOptions: { signal },
            }),
          ).body,
        staleTime: Infinity,
      });
    }),
  );
  return rings.flat();
}
